/**
 * Background prefetch for Hired Profiles page.
 * After login, data is fetched in the background so the page loads faster.
 * Cache is in-memory with a short TTL; cleared on logout.
 */

import { getApiUrl } from '../../../config/api';

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

let cache = {
  jobs: null,
  hiredCandidatesByJobId: null,
  timestamp: 0,
};

function getCompanyEmail() {
  return localStorage.getItem('employer_email') || sessionStorage.getItem('employer_email');
}

function getCompanyId() {
  return (
    localStorage.getItem('companyAccountId') ||
    localStorage.getItem('companyId') ||
    localStorage.getItem('companyID') ||
    ''
  );
}

function isCacheValid() {
  return cache.timestamp && Date.now() - cache.timestamp < CACHE_TTL_MS;
}

/**
 * Returns cached jobs (with hired count) if valid.
 */
export function getCachedHiredJobs() {
  if (!cache.jobs || !isCacheValid()) return null;
  return cache.jobs;
}

/**
 * Returns cached hired candidates for a job if valid.
 */
export function getCachedHiredCandidates(jobId) {
  if (!jobId || !cache.hiredCandidatesByJobId || !isCacheValid()) return null;
  return cache.hiredCandidatesByJobId[jobId] ?? null;
}

/**
 * Clear cache (e.g. on logout).
 */
export function clearHiredProfilesPrefetchCache() {
  cache = { jobs: null, hiredCandidatesByJobId: null, timestamp: 0 };
}

/**
 * Start prefetching Hired Profiles data in the background (fire-and-forget).
 */
export function startHiredProfilesPrefetch() {
  const companyEmail = getCompanyEmail();
  const companyId = getCompanyId();
  if (!companyEmail) return;

  const jobsUrl = getApiUrl(
    `/api/get_jobs_with_hired_count?company_email=${encodeURIComponent(companyEmail)}${companyId ? `&company_id=${companyId}` : ''}`
  );

  fetch(jobsUrl, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data?.success || !Array.isArray(data.jobs)) return;
      cache.jobs = data.jobs;
      cache.timestamp = Date.now();
      if (!cache.hiredCandidatesByJobId) cache.hiredCandidatesByJobId = {};

      const jobWithHires = data.jobs.find((j) => (j.hired_count || 0) > 0);
      const firstJobId = jobWithHires?.job_id ?? data.jobs[0]?.job_id;
      if (!firstJobId) return;

      const candidatesUrl = getApiUrl(
        `/api/get_hired_candidates?company_email=${encodeURIComponent(companyEmail)}&job_id=${firstJobId}${companyId ? `&company_id=${companyId}` : ''}`
      );
      fetch(candidatesUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((candidateData) => {
          if (candidateData?.success && Array.isArray(candidateData.hired_candidates)) {
            cache.hiredCandidatesByJobId[firstJobId] = candidateData.hired_candidates;
          }
        })
        .catch(() => {});
    })
    .catch(() => {});
}
